import { create } from "zustand";
import type { Platform } from "@/services/device-options/device-options.types";
import {
  DeliveryMethod,
  NO_KEYBOARD_LAYOUT,
  PickupPhone,
  PickupSelection,
  ReplacementDraft,
  ValidatedAddress,
} from "./replacement.types";

interface ReplacementDraftActions {
  resetDraft: () => void;
  selectPlatform: (platform: Platform) => void;
  chooseDevice: (deviceId: string, offeredLayouts: string[]) => void;
  setKeyboardLayout: (layout: string) => void;
  chooseDeliveryMethod: (method: DeliveryMethod) => void;
  selectPickup: (pickup: PickupSelection) => void;
  setPickupPhone: (pickupPhone: PickupPhone) => void;
  setShippingAddress: (address: ValidatedAddress) => void;
  startEditing: () => void;
  stopEditing: () => void;
  setHomeAddress: (address: ValidatedAddress) => void;
}

export const initialReplacementDraftState: ReplacementDraft = {
  platform: undefined,
  deviceId: undefined,
  keyboardLayout: undefined,
  deliveryMethod: undefined,
  pickup: undefined,
  pickupPhone: undefined,
  shippingAddress: undefined,
  isEditing: false,
  homeAddress: undefined,
};

const clearedDeviceDependentDelivery: Pick<ReplacementDraft, "deliveryMethod" | "pickup"> = {
  deliveryMethod: undefined,
  pickup: undefined,
};

const reconcileLayout = (current: string | undefined, offeredLayouts: string[]): string => {
  if (offeredLayouts.length === 0) return NO_KEYBOARD_LAYOUT;
  if (current && offeredLayouts.includes(current)) return current;
  return offeredLayouts[0];
};

export const useReplacementDraftStore = create<ReplacementDraft & ReplacementDraftActions>(
  (set) => ({
    ...initialReplacementDraftState,
    resetDraft: () => set(initialReplacementDraftState),
    selectPlatform: (platform) =>
      set({
        platform,
        deviceId: undefined,
        keyboardLayout: undefined,
        ...clearedDeviceDependentDelivery,
      }),
    chooseDevice: (deviceId, offeredLayouts) =>
      set((state) => ({
        deviceId,
        keyboardLayout: reconcileLayout(state.keyboardLayout, offeredLayouts),
        ...(state.deviceId === deviceId ? {} : clearedDeviceDependentDelivery),
      })),
    setKeyboardLayout: (layout) => set({ keyboardLayout: layout }),
    chooseDeliveryMethod: (method) => set({ deliveryMethod: method }),
    selectPickup: (pickup) => set({ pickup }),
    setPickupPhone: (pickupPhone) => set({ pickupPhone }),
    setShippingAddress: (address) => set({ shippingAddress: address }),
    startEditing: () => set({ isEditing: true }),
    stopEditing: () => set({ isEditing: false }),
    setHomeAddress: (address) => set({ homeAddress: address }),
  })
);
